import { useParams, Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { useSupabaseDocsPost, useSupabaseDocsGrouped } from '@/hooks/useSupabaseDocs';
import { sanitizeHtml } from '@/utils/sanitizeHtml';
import SEO from '@/components/SEO';
import { supabaseDocsService } from '@/services/supabaseDocsService';
import { useMemo } from 'react';
import SupportExpectationsBox from '@/components/support/SupportExpectationsBox';
import thriveScorePreview from '@/assets/thrivescore-preview.png';
import { ThriveScore } from '@/components/ui/thrivescore';

// Slugs that show a hero image at the top
const HERO_IMAGES: Record<string, { src: string; alt: string }> = {
  'understanding-your-thrivescore': { src: thriveScorePreview, alt: 'ThriveScore showing a score of 40 with blue ring and orange shimmer' },
};

// Slugs that show the animated ThriveScore widget
const THRIVESCORE_WIDGET_SLUGS = ['thrivescore-history'];

// Per-slug expectations data for action-oriented pages

const EXPECTATIONS: Record<string, string[]> = {
  'create-your-seniorthrive-account': [
    'Visit the sign-up page',
    'Enter your name and email',
    'Create a secure password',
    'Confirm your email',
  ],
  'what-you-get-7-day-trial': [
    'Run up to 5 room safety scans',
    'Upload or take up to 5 room images',
    'Complete daily wellness check-ins',
    'View your ThriveScore',
    'Invite one family member',
  ],
  'quick-profile': [
    'Answer a few simple questions',
    'Share basic information about your home and mobility',
    'Receive personalized guidance',
    'Update your answers anytime',
  ],
  'understanding-your-dashboard': [
    'See your current ThriveScore',
    'Access room safety scans',
    'Complete your MyDaily Check-In',
    'Invite a family member',
  ],
  'your-first-24-hours-on-seniorthrive': [
    'Complete your Quick Profile',
    'Run your first room safety scan',
    'Review your ThriveScore',
    'Try a MyDaily Check-In',
  ],
  'daily-wellness-log': [
    'Choose which lifestyle factors to track',
    'Add daily entries for hydration, mood, or sleep',
    'Review your patterns over time',
    'Export or share your log',
  ],
  'vital-signs': [
    'Select which vitals to monitor',
    'Log readings like blood pressure or weight',
    'View trends and stability indicators',
    'Share your history with a provider',
  ],
  'symptoms-how-am-i-feeling': [
    'Log symptoms like pain, fatigue, or dizziness',
    'Rate severity for each entry',
    'Spot patterns alongside your vitals',
    'Share with your care team',
  ],
  'reviewing-history-and-sharing': [
    'View trend charts and filtered history',
    'Adjust what you track at any time',
    'Export logs for appointments',
    'Share with ThriveCircle or providers',
  ],
};

const SupportOlderAdultsArticle = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data: doc, isLoading, error } = useSupabaseDocsPost(slug || '');
  const { data: grouped } = useSupabaseDocsGrouped('older-adults');

  // Find prev/next within the same topic_type
  const { prevStep, nextStep } = useMemo(() => {
    if (!doc || !grouped || !doc.topic_type) return { prevStep: null, nextStep: null };
    const topicDocs = grouped[doc.topic_type]?.docs || [];
    const idx = topicDocs.findIndex(d => d.slug === slug);
    return {
      prevStep: idx > 0 ? topicDocs[idx - 1] : null,
      nextStep: idx >= 0 && idx < topicDocs.length - 1 ? topicDocs[idx + 1] : null,
    };
  }, [doc, grouped, slug]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-12">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse space-y-6">
              <div className="h-4 bg-muted rounded w-48"></div>
              <div className="h-12 bg-muted rounded w-3/4"></div>
              <div className="h-64 bg-muted rounded"></div>
              <div className="space-y-3">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="h-4 bg-muted rounded"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Article Not Found</h1>
          <p className="text-muted-foreground mb-6">
            The support article you're looking for doesn't exist or has been moved.
          </p>
          <Link to="/support/older-adults" className="text-primary hover:underline">
            ← Back to Older Adult Support
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`${doc.title} - Older Adult Support - SeniorThrive`}
        description={doc.excerpt || supabaseDocsService.stripHtml(doc.content).substring(0, 160)}
      />

      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <Link
              to="/support/older-adults"
              className="inline-flex items-center text-primary hover:underline mb-8"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Older Adult Support
            </Link>

            <header className="mb-8">
              <h1 className="text-4xl font-bold text-foreground mb-4">
                {doc.title}
              </h1>
            </header>
            {slug && HERO_IMAGES[slug] && (
              <div className="flex justify-center mb-10">
                <img
                  src={HERO_IMAGES[slug].src}
                  alt={HERO_IMAGES[slug].alt}
                  className="rounded-2xl shadow-md max-w-sm w-full"
                />
              </div>
            )}
            {slug && THRIVESCORE_WIDGET_SLUGS.includes(slug) && (
              <div className="flex flex-col items-center mb-10 py-10 px-8 overflow-visible bg-gradient-to-br from-blue-50 to-orange-50 dark:from-blue-950/20 dark:to-orange-950/20 rounded-2xl shadow-sm">
                <div className="p-4">
                  <ThriveScore startScore={0} targetScore={245} size={200} animate={true} scoreTextSize="text-5xl" />
                </div>
                <p className="text-sm text-muted-foreground mt-4 text-center max-w-xs">
                  Your ThriveScore combines <span className="font-semibold text-primary">Home Safety</span> and <span className="font-semibold text-orange-500">Activity &amp; Wellness</span> into one number.
                </p>
              </div>
            )}

            <article className="prose prose-lg max-w-none">
              {(() => {
                const expectations = slug ? EXPECTATIONS[slug] : null;
                const htmlContent = sanitizeHtml(doc.content, {
                  allowLinks: true,
                  allowBasicFormatting: true,
                  allowImages: true
                });

                if (expectations) {
                  // Split at first <h2> to insert expectations box between intro and steps
                  const firstH2 = htmlContent.indexOf('<h2>');
                  const introPart = firstH2 > 0 ? htmlContent.substring(0, firstH2) : '';
                  const restPart = firstH2 > 0 ? htmlContent.substring(firstH2) : htmlContent;

                  const contentClasses = `
                    [&>h2]:text-2xl [&>h2]:font-semibold [&>h2]:mt-10 [&>h2]:mb-4 [&>h2]:text-primary
                    [&>h3]:text-xl [&>h3]:font-semibold [&>h3]:mt-6 [&>h3]:mb-3
                    [&>p]:mb-4 [&>p]:leading-relaxed [&>p]:text-base
                    [&>ul]:mb-4 [&>ul]:pl-6 [&>ul>li]:mb-2 [&>ul>li]:list-disc
                    [&>ol]:mb-4 [&>ol]:pl-6 [&>ol>li]:mb-2 [&>ol>li]:list-decimal
                    [&>a]:text-primary [&>a]:underline [&>a]:hover:no-underline
                    [&>img]:rounded-lg [&>img]:my-6 [&>img]:w-full
                    [&>.reassurance]:bg-muted/30 [&>.reassurance]:rounded-lg [&>.reassurance]:p-6 [&>.reassurance]:mt-10 [&>.reassurance]:border
                    [&>.reassurance>p]:text-muted-foreground [&>.reassurance>p]:mb-2 [&>.reassurance>p:last-child]:mb-0 [&>.reassurance>p]:font-medium
                  `;

                  return (
                    <>
                      {introPart && (
                        <div dangerouslySetInnerHTML={{ __html: introPart }} className={contentClasses} />
                      )}
                      <SupportExpectationsBox items={expectations} />
                      <div dangerouslySetInnerHTML={{ __html: restPart }} className={contentClasses} />
                    </>
                  );
                }

                return (
                  <div
                    dangerouslySetInnerHTML={{ __html: htmlContent }}
                    className="
                      [&>h2]:text-2xl [&>h2]:font-semibold [&>h2]:mt-10 [&>h2]:mb-4 [&>h2]:text-primary
                      [&>h3]:text-xl [&>h3]:font-semibold [&>h3]:mt-6 [&>h3]:mb-3
                      [&>p]:mb-4 [&>p]:leading-relaxed [&>p]:text-base
                      [&>ul]:mb-4 [&>ul]:pl-6 [&>ul>li]:mb-2 [&>ul>li]:list-disc
                      [&>ol]:mb-4 [&>ol]:pl-6 [&>ol>li]:mb-2 [&>ol>li]:list-decimal
                      [&>a]:text-primary [&>a]:underline [&>a]:hover:no-underline
                      [&>img]:rounded-lg [&>img]:my-6 [&>img]:w-full
                      [&>.reassurance]:bg-muted/30 [&>.reassurance]:rounded-lg [&>.reassurance]:p-6 [&>.reassurance]:mt-10 [&>.reassurance]:border
                      [&>.reassurance>p]:text-muted-foreground [&>.reassurance>p]:mb-2 [&>.reassurance>p:last-child]:mb-0 [&>.reassurance>p]:font-medium
                    "
                  />
                );
              })()}
            </article>

            {(prevStep || nextStep) && (
              <nav className="mt-12 pt-8 border-t flex items-stretch gap-4">
                {prevStep ? (
                  <Link
                    to={`/support/older-adults/${prevStep.slug}`}
                    className="flex-1 group rounded-lg border p-5 hover:border-primary/40 hover:bg-muted/40 transition-colors"
                  >
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      ← Previous Step
                    </span>
                    <p className="mt-1 font-semibold text-foreground group-hover:text-primary transition-colors">
                      {prevStep.title}
                    </p>
                  </Link>
                ) : <div className="flex-1" />}
                {nextStep ? (
                  <Link
                    to={`/support/older-adults/${nextStep.slug}`}
                    className="flex-1 group rounded-lg border p-5 hover:border-primary/40 hover:bg-muted/40 transition-colors text-right"
                  >
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      Next Step →
                    </span>
                    <p className="mt-1 font-semibold text-foreground group-hover:text-primary transition-colors">
                      {nextStep.title}
                    </p>
                  </Link>
                ) : null}
              </nav>
            )}

            <footer className="mt-10 pt-8 border-t">
              <div className="bg-muted/30 rounded-lg p-6">
                <h3 className="text-lg font-semibold mb-2">Need Help?</h3>
                <p className="text-muted-foreground mb-4">
                  Need help creating your account? Our support team is ready to assist you.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center text-primary hover:underline font-medium"
                >
                  Contact Support →
                </Link>
              </div>
            </footer>
          </div>
        </div>
      </div>
    </>
  );
};

export default SupportOlderAdultsArticle;
